/**
 * Shared Stripe fee calculator (frontend mirror).
 *
 * Mirrors backend `api_backend_fastify_server/src/utils/stripe-fee-calculator.ts`.
 * Used to render a live, read-only transaction fee under the entry-fee
 * input in the event creation/edit form. The backend re-derives the same
 * value at registration time so the displayed estimate matches what the
 * payer is actually charged.
 *
 * Formula: gross = (entry + fixed) / (1 - pct); fee = gross - entry,
 * rounded UP to the nearest cent so the platform always nets the full
 * entry fee.
 */

export interface StripeFeeConfig {
  percentage_rate: number;
  fixed_fee_cents: number;
  enabled: boolean;
  currency: string;
}

export function calculateTransactionFee(
  entryFeeDollars: number,
  config: Pick<StripeFeeConfig, 'percentage_rate' | 'fixed_fee_cents' | 'enabled'>,
): number {
  if (!config.enabled) return 0;
  if (!Number.isFinite(entryFeeDollars) || entryFeeDollars <= 0) return 0;
  if (config.percentage_rate >= 1 || config.percentage_rate < 0) return 0;

  const fixedFee = config.fixed_fee_cents / 100;
  const gross = (entryFeeDollars + fixedFee) / (1 - config.percentage_rate);
  const fee = gross - entryFeeDollars;

  return Math.ceil(fee * 100) / 100;
}
